import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, TrendingUp, Zap } from "lucide-react";
import { useState } from "react";

import { useTranslation } from "@/core/i18n";

type AgingReportKeyInsight = {
  title: string;
  description: string;
  icon: string;
};

type AgingReportData = {
  headline: string;
  agingScience: string;
  treatmentBenefits: string[];
  withoutTreatmentPreview: string;
  withTreatmentPreview: string;
  keyInsights: AgingReportKeyInsight[];
  yearsYoungerEstimate: number;
};

type AgingReportSectionProps = {
  agingReport: AgingReportData;
};

export function AgingReportSection({ agingReport }: Readonly<AgingReportSectionProps>) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const subtitle = agingReport.yearsYoungerEstimate
    ? t("agingReportSection.subtitleYearsYounger", {
        years: agingReport.yearsYoungerEstimate,
      })
    : t("agingReportSection.subtitleDefault");

  return (
    <motion.div
      className="overflow-hidden rounded-xl border border-gray-200 bg-white"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 p-4 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <TrendingUp className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-body1 font-semibold text-primary">{t("agingReportSection.title")}</p>
          <p className="text-xs text-secondary">{subtitle}</p>
        </div>
        <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="h-5 w-5 text-secondary" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-4 px-4 pb-4">
              {agingReport.headline ? (
                <p className="text-body1 font-medium text-primary">{agingReport.headline}</p>
              ) : null}

              {agingReport.agingScience ? (
                <p className="text-xs leading-relaxed text-secondary">{agingReport.agingScience}</p>
              ) : null}

              {agingReport.treatmentBenefits.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-primary">
                    {t("agingReportSection.treatmentBenefits")}
                  </p>
                  <ul className="space-y-2">
                    {agingReport.treatmentBenefits.map((benefit) => (
                      <li className="flex items-start gap-2 text-sm text-secondary" key={benefit}>
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(agingReport.withoutTreatmentPreview || agingReport.withTreatmentPreview) && (
                <div className="grid grid-cols-2 gap-3">
                  {agingReport.withoutTreatmentPreview ? (
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                      <p className="mb-1 text-xs font-medium text-primary">
                        {t("agingReportSection.withoutCare")}
                      </p>
                      <p className="text-xs text-amber-600/80">
                        {agingReport.withoutTreatmentPreview}
                      </p>
                    </div>
                  ) : null}
                  {agingReport.withTreatmentPreview ? (
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                      <p className="mb-1 text-xs font-medium text-primary">
                        {t("agingReportSection.withCare")}
                      </p>
                      <p className="text-xs text-emerald-600/80">
                        {agingReport.withTreatmentPreview}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}

              {agingReport.keyInsights.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-primary">
                    {t("agingReportSection.keyInsights")}
                  </p>
                  <div className="space-y-2">
                    {agingReport.keyInsights.map((insight) => (
                      <div
                        className="flex items-start gap-3 rounded-xl bg-gray-50 p-3"
                        key={insight.title}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <Zap className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          {insight.title ? (
                            <p className="text-xs font-medium text-primary">{insight.title}</p>
                          ) : null}
                          {insight.description ? (
                            <p className="text-xs text-secondary">{insight.description}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
